"use client";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@workspace/ui/components/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@workspace/ui/components/drawer";
import { useIsMobile } from "@workspace/ui/hooks/use-mobile";
import { cn } from "@workspace/ui/lib/utils";
import {
  Children,
  type ComponentProps,
  createContext,
  isValidElement,
  type ReactNode,
  use,
} from "react";

type ResponsiveDialogContextValue = {
  isMobile: boolean;
};

const MOBILE_RESPONSIVE_DIALOG_CONTEXT = {
  isMobile: true,
} as const satisfies ResponsiveDialogContextValue;

const DESKTOP_RESPONSIVE_DIALOG_CONTEXT = {
  isMobile: false,
} as const satisfies ResponsiveDialogContextValue;

const ResponsiveDialogContext =
  createContext<ResponsiveDialogContextValue | null>(null);

function useResponsiveDialogContext() {
  const context = use(ResponsiveDialogContext);
  if (!context) {
    throw new Error(
      "ResponsiveDialog components must be used within ResponsiveDialog"
    );
  }
  return context;
}

function splitContentChildren(children: ReactNode) {
  const items = Children.toArray(children);
  const footerIndex = items.findIndex(
    (child) => isValidElement(child) && child.type === ResponsiveDialogFooter
  );

  if (footerIndex === -1) {
    return { body: children, footer: null };
  }

  return {
    body: items.filter((_, index) => index !== footerIndex),
    footer: items[footerIndex],
  };
}

type ResponsiveDialogProps = ComponentProps<typeof Dialog> & {
  /**
   * Renders the Dialog variant even on mobile. Needed when the dialog opens
   * above an already-open drawer, where a second non-nested vaul drawer
   * fights over body styles and gestures. For true simultaneous drawer
   * nesting, vaul's Drawer.NestedRoot would be the option instead.
   */
  forceDialog?: boolean;
};

function ResponsiveDialog({
  children,
  forceDialog = false,
  ...props
}: ResponsiveDialogProps) {
  const isMobile = useIsMobile() && !forceDialog;

  if (isMobile) {
    return (
      <ResponsiveDialogContext.Provider
        value={MOBILE_RESPONSIVE_DIALOG_CONTEXT}
      >
        <Drawer data-slot="responsive-dialog" {...props}>
          {children}
        </Drawer>
      </ResponsiveDialogContext.Provider>
    );
  }

  return (
    <ResponsiveDialogContext.Provider value={DESKTOP_RESPONSIVE_DIALOG_CONTEXT}>
      <Dialog data-slot="responsive-dialog" {...props}>
        {children}
      </Dialog>
    </ResponsiveDialogContext.Provider>
  );
}

function ResponsiveDialogTrigger({
  ...props
}: ComponentProps<typeof DialogTrigger>) {
  const { isMobile } = useResponsiveDialogContext();

  if (isMobile) {
    return <DrawerTrigger data-slot="responsive-dialog-trigger" {...props} />;
  }

  return <DialogTrigger data-slot="responsive-dialog-trigger" {...props} />;
}

function ResponsiveDialogContent({
  children,
  className,
  showCloseButton,
  ...props
}: ComponentProps<typeof DialogContent>) {
  const { isMobile } = useResponsiveDialogContext();

  if (isMobile) {
    const { body, footer } = splitContentChildren(children);

    return (
      <DrawerContent
        className={cn("max-h-[calc(100dvh-4rem)] gap-0 p-0", className)}
        data-slot="responsive-dialog-content"
        {...props}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pt-2 pb-4">
          {body}
        </div>
        {footer}
      </DrawerContent>
    );
  }

  return (
    <DialogContent
      className={className}
      data-slot="responsive-dialog-content"
      showCloseButton={showCloseButton}
      {...props}
    >
      {children}
    </DialogContent>
  );
}

function ResponsiveDialogHeader({
  className,
  ...props
}: ComponentProps<typeof DialogHeader>) {
  const { isMobile } = useResponsiveDialogContext();

  if (isMobile) {
    return (
      <DrawerHeader
        className={cn(
          "px-0 text-left group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left group-data-[vaul-drawer-direction=top]/drawer-content:text-left",
          className
        )}
        data-slot="responsive-dialog-header"
        {...props}
      />
    );
  }

  return (
    <DialogHeader
      className={cn("text-left", className)}
      data-slot="responsive-dialog-header"
      {...props}
    />
  );
}

function ResponsiveDialogFooter({
  className,
  ...props
}: ComponentProps<typeof DialogFooter>) {
  const { isMobile } = useResponsiveDialogContext();

  if (isMobile) {
    return (
      <DrawerFooter
        className={cn("border-t bg-background px-4 pt-2 pb-4", className)}
        data-slot="responsive-dialog-footer"
        {...props}
      />
    );
  }

  return (
    <DialogFooter
      className={className}
      data-slot="responsive-dialog-footer"
      {...props}
    />
  );
}

function ResponsiveDialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogTitle>) {
  const { isMobile } = useResponsiveDialogContext();

  if (isMobile) {
    return (
      <DrawerTitle
        className={cn("text-lg", className)}
        data-slot="responsive-dialog-title"
        {...props}
      />
    );
  }

  return (
    <DialogTitle
      className={className}
      data-slot="responsive-dialog-title"
      {...props}
    />
  );
}

function ResponsiveDialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogDescription>) {
  const { isMobile } = useResponsiveDialogContext();

  if (isMobile) {
    return (
      <DrawerDescription
        className={className}
        data-slot="responsive-dialog-description"
        {...props}
      />
    );
  }

  return (
    <DialogDescription
      className={className}
      data-slot="responsive-dialog-description"
      {...props}
    />
  );
}

function ResponsiveDialogClose({
  ...props
}: ComponentProps<typeof DialogClose>) {
  const { isMobile } = useResponsiveDialogContext();

  if (isMobile) {
    return <DrawerClose data-slot="responsive-dialog-close" {...props} />;
  }

  return <DialogClose data-slot="responsive-dialog-close" {...props} />;
}

export {
  ResponsiveDialog,
  ResponsiveDialogTrigger,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
};
